import { parseISO } from 'date-fns';
import * as React from 'react';
import { SeverityBadge } from '../Situation/SeverityBadge';
import { TimeElement } from '../TimeElement/TimeElement';
import { capitalizeFirstLetter } from '../Util';
import formatCloseRelative from '../Util/formatCloseRelative';
import { getSvgIconId } from '../Util/getSvgIconId';
import { AlertType } from './Alerts.types';

export interface IAlertsProps {
    region: string;
    alerts: Alert[];
}

interface IAlertsState {
    alerts: Alert[];
    currentAlert: Alert;
    closedAlerts: string[];
    loaded: boolean;
}

class Alert {
    header: string;
    id: number;
    closed: boolean;
    name: string;
    title: string;
    message: string;
    additionalMessage: string;
    startTime?: Date;
    endTime?: Date;
    versionTime?: Date;
    alertVersionTime?: Date;
    iconId: number;
    provider: string;
    situationId: number;
    showOnInit: boolean;
    showedOnInit: boolean;
    type: AlertType;
    messageCode: string;
    location: string;
    severity: number;
    severityText: string;
    trafficType: string;
}

export class Alerts extends React.Component<IAlertsProps, IAlertsState> {
    _timer: ReturnType<typeof setTimeout>;

    constructor(props: IAlertsProps) {
        super(props);
        this.state = { alerts: this.props.alerts, currentAlert: null, closedAlerts: [], loaded: false };
    }

    componentDidMount(): void {
        this.setState({
            closedAlerts: this.getClosedAlerts(),
            loaded: true,
        });

        setTimeout(() => {
            this.checkInitialPopup();
        }, 500);

        this._scheduleUpdate();
        document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }

    componentWillUnmount(): void {
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        this._stopUpdates();
    }

    _scheduleUpdate() {
        if (!this._timer) {
            this._timer = setTimeout(() => {
                this._timer = null;
                this.updateAlerts();
            }, 60000);
        }
    }

    _stopUpdates() {
        clearTimeout(this._timer);
        this._timer = null;
    }

    _handleVisibilityChange = () => {
        if (document.hidden) {
            this._stopUpdates();
        } else {
            this.updateAlerts();
        }
    };

    render(): JSX.Element {
        if (this.state.alerts == null) {
            return null;
        }

        const alerts: Alert[] = this.state.alerts;
        const hidden = this.state.closedAlerts;

        const empty = alerts.filter((x) => !x.closed).length === 0;
        const allhidden = hidden.filter((x) => alerts.filter((y) => y.id.toString() + y.alertVersionTime === x).length > 0).length === alerts.length;

        if (empty || allhidden) {
            return null;
        }

        return (
            <div className="alert-bar-container">
                {alerts.map((item, index) => this.renderAlert(item, index))}
                <div className="modal fade" id="alertModal" tabIndex={-1} role="dialog" aria-labelledby="alertModalLabel" aria-hidden="true">
                    <div className="modal-dialog" role="dialog">
                        {this.state.currentAlert ? this.renderPopup(this.state.currentAlert) : null}
                    </div>
                </div>
            </div>
        );
    }

    private updateAlerts() {
        fetch('/api/alerts?region=' + this.props.region.toLowerCase())
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
            })
            .then((data) => {
                this.setNewAlerts(data);
                setTimeout(() => {
                    this.checkInitialPopup();
                }, 500);
            })
            .finally(() => {
                this._scheduleUpdate();
            });
    }

    private closeAlert(alert: Alert) {
        const alerts = this.state.alerts.map((x) => {
            x.closed = x.closed || x.id === alert.id;

            return x;
        });
        this.addClosedAlertToStorage(alert);
        this.setNewAlerts(alerts);

        if (window !== undefined) {
            window.dispatchEvent(new Event('resize'));
        }
    }

    private addClosedAlertToStorage(alert: Alert) {
        const closedAlerts = this.state.closedAlerts;
        closedAlerts.push(alert.id.toString() + alert.alertVersionTime);

        sessionStorage.setItem('closedAlerts', JSON.stringify(closedAlerts));
        this.setState({
            closedAlerts,
        });
    }

    private getClosedAlerts() {
        if (!sessionStorage.getItem('closedAlerts')) {
            return [];
        }

        return JSON.parse(sessionStorage.getItem('closedAlerts'));
    }

    private checkInitialPopup() {
        const alerts = this.state.alerts;
        const closedAlerts = this.state.closedAlerts;

        for (const alert of alerts) {
            if (alert.showOnInit && !alert.showedOnInit && closedAlerts.indexOf(alert.id.toString() + alert.alertVersionTime) === -1) {
                this.showPopup(alert);
                alert.showedOnInit = true;
                break;
            }
        }

        this.setState({ alerts });
    }

    private setNewAlerts(alerts: Alert[]) {
        if (this.state.alerts == null) {
            this.setState({ alerts });
            return;
        }

        const current: Alert[] = this.state.alerts;

        for (const alert of alerts) {
            const oldAlert: Alert[] = current.filter((x) => x.id === alert.id);
            if (oldAlert.length > 0 && !alert.closed) {
                alert.closed = oldAlert[0].closed;
            }
            if (oldAlert.length > 0) {
                alert.showedOnInit = oldAlert[0].showedOnInit;
            }
        }

        const currentAlert: Alert = this.state.currentAlert;

        if (currentAlert !== null && alerts.filter((x) => x.id === currentAlert.id).length === 0) {
            this.closePopup();
        }

        this.setState({ alerts });
    }

    private closePopup() {
        this.setState({ currentAlert: null });
    }

    private showPopup(alert) {
        this.setState({ currentAlert: alert });
    }
    private getOccurrenceText(alert: Alert) {
        const currenDate = new Date();
        let occurrenceText;
        if (alert.messageCode === 'Viktig trafikinformation') {
            occurrenceText = 'Viktig trafikinformation';
        } else if (alert.trafficType === 'Cykeltrafik') {
            occurrenceText = 'Gång- och cykeltrafik';
        } else {
            occurrenceText = alert.trafficType + ', ';
            occurrenceText +=
                new Date(alert.startTime) <= currenDate && (alert.endTime == null || new Date(alert.endTime) >= currenDate)
                    ? 'pågående händelse'
                    : new Date(alert.startTime) >= currenDate
                      ? 'kommande händelse'
                      : '';
        }

        return occurrenceText;
    }
    private renderAlert(item: Alert, index: number): JSX.Element {
        if (item.closed || this.state.closedAlerts.filter((x) => x === item.id.toString() + item.alertVersionTime).length > 0 || !this.state.loaded) {
            return null;
        }

        const readMore = () => this.showPopup(item);
        const close = () => this.closeAlert(item);

        const colorClass = item.type === AlertType.operation ? 'alert-bar--operation' : 'alert-bar--situation';

        return (
            <div key={index} className={`alert-bar d-flex align-items-center justify-content-between p-2 ${colorClass}`} aria-haspopup="true">
                <button
                    type="button"
                    className="unstyled-button alert-bar__button text-start text-md-center w-100"
                    onClick={readMore}
                    data-bs-toggle="modal"
                    data-bs-target="#alertModal"
                >
                    {item.title}
                </button>
                <button type="button" className="unstyled-button alert-bar__cancel p-2 flex-shrink-0" onClick={close}>
                    Stäng
                    <i className="icon-cancel d-none d-md-inline" />
                    <i className="icon-cancel d-md-none justify-content-center" />
                </button>
            </div>
        );
    }

    private renderPopup(alert) {
        const icon = alert.iconId ? (
            <span className="alerticon" aria-hidden="true">
                <svg className="svg-icon">
                    <use xlinkHref={getSvgIconId(alert.iconId)} />
                </svg>
            </span>
        ) : null;
        const situationButton = alert.detailsPath ? (
            <div className="buttons">
                <a className="button" title="Mer information" href={alert.detailsPath}>
                    {' '}
                    Mer information
                </a>
            </div>
        ) : null;

        const colorClass = alert.type === AlertType.operation ? 'alert-modal--operation' : 'alert-modal--situation';
        const startTimeISO = alert.startTime && parseISO(alert.startTime);
        const endTimeISO = alert.endTime && parseISO(alert.endTime);

        return (
            <div className={`modal-content alert-modal ${colorClass}`}>
                <div className="modal-header alert-modal__header">
                    <h5 className="mb-0" id="alertModalLabel">
                        {alert.title}
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal">
                        <span className="visually-hidden">Stäng</span>
                    </button>
                </div>
                <div className="modal-body alert-modal__body">
                    {alert.type === 'Situation' ? (
                        <div className="d-flex align-items-center mb-3">
                            {icon}
                            <b>{this.getOccurrenceText(alert)}</b>
                        </div>
                    ) : null}
                    <div className="main-content">
                        <h6>{alert.location}</h6>
                        {alert.type === 'Situation' ? <span className="message-code">{alert.messageCode} · </span> : null}
                        <span className="alert-modal__message" dangerouslySetInnerHTML={{ __html: alert.message }} />
                        {alert.type === 'Situation' ? (
                            <p className="alert-modal__additionalMessage" dangerouslySetInnerHTML={{ __html: alert.additionalMessage }} />
                        ) : null}
                        {alert.severityText ? <SeverityBadge severity={alert.severity} severityText={alert.severityText} /> : null}
                        {alert.type === 'Situation' ? (
                            <table className="traffic-info-table">
                                <tbody>
                                    {alert.startTime != null ? (
                                        <tr>
                                            <td className="traffic-info-table-header">Giltig från:</td>
                                            <td>
                                                <TimeElement date={startTimeISO}>
                                                    {capitalizeFirstLetter(formatCloseRelative(startTimeISO))}
                                                </TimeElement>
                                            </td>
                                        </tr>
                                    ) : null}
                                    {alert.endTime != null ? (
                                        <tr>
                                            <td className="traffic-info-table-header">Beräknas pågå till:</td>
                                            <TimeElement date={endTimeISO}>{capitalizeFirstLetter(formatCloseRelative(endTimeISO))}</TimeElement>
                                        </tr>
                                    ) : null}
                                    {alert.provider != null && alert.provider !== '' ? (
                                        <tr>
                                            <td className="traffic-info-table-header">Källa</td>
                                            <td>{alert.provider}</td>
                                        </tr>
                                    ) : null}
                                </tbody>
                            </table>
                        ) : null}
                        {situationButton}
                    </div>
                </div>
            </div>
        );
    }
}
